// frontend/packages/business-console/src/pages/ServicePageAuthor.jsx
//
// Conversational CMS copilot - service-page authoring (plan 3.1.4 / PR #2).
// The sibling of BlogAuthor for STRUCTURED pages: the client describes a
// service in a sentence, the backend (/api/v2/site-pages) generates a
// schema-valid, gate-clean `services/{slug}.md` by reusing the build
// generator, and the client saves it to the uat draft, previews it on the real
// themed site, and publishes. The draft write + publish reuse the existing
// content-editor service (same boundary as the blog writer; no Git logic here).
//
// A guided form, not a chat: the backend endpoint is a single non-streaming
// generate. Regenerating replaces the draft; the client edits the brief, not
// the page structure (D24: the platform owns the structure).

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import PublishRoundedIcon from '@mui/icons-material/PublishRounded';

import { composeServicePage } from '../services/sitePageAuthoringService';
import {
  writeContentDraft,
  publishContent,
  splitFrontmatter,
  listContent,
} from '../services/contentEditorService';
import { getSitePreviewUrls } from '../services/sitePreviewService';

function errMessage(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback;
}

// Mirrors the backend slugify (crm/build-funnel/lib copyOrchestrator.slugify)
// so the duplicate pre-check computes the same services/{slug}.md path the
// server writes. Keep in sync if the backend rule changes.
function slugify(name) {
  return String(name || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

/** The page body (after the frontmatter fence) for a read-only preview. */
function bodyOf(markdown) {
  return splitFrontmatter(markdown || '').body || '';
}

export default function ServicePageAuthor({ embedded = false }) {
  const [serviceName, setServiceName] = useState('');
  const [serviceDescription, setServiceDescription] = useState('');

  const [generating, setGenerating] = useState(false);
  const [page, setPage] = useState(null); // { slug, path, markdown, frontmatter }
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [savedPath, setSavedPath] = useState('');
  const [savedSha, setSavedSha] = useState('');
  const [dirtySinceSave, setDirtySinceSave] = useState(false);
  const [saving, setSaving] = useState(false);

  const [previewed, setPreviewed] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [previewUrls, setPreviewUrls] = useState(null);
  const loadedUrls = useRef(false);

  useEffect(() => {
    if (loadedUrls.current) return;
    loadedUrls.current = true;
    getSitePreviewUrls()
      .then((u) => setPreviewUrls(u))
      .catch(() => {});
  }, []);

  const canGenerate =
    serviceName.trim().length > 0 &&
    serviceDescription.trim().length > 0 &&
    !generating;
  const canSave = !!page && !generating && (dirtySinceSave || !savedPath);
  const canPublish =
    !!savedPath && !dirtySinceSave && previewed && !publishing && !published;

  const handleGenerate = useCallback(async () => {
    if (!canGenerate) return;
    setGenerating(true);
    setError('');
    setNotice('');
    try {
      // Duplicate-slug pre-check. Creating a service whose slug already exists
      // fails with an opaque 422 at save time (GitHub needs a sha to update an
      // existing file), AFTER the paid generation already ran. Catch it up
      // front. Degrade-open: if the list can't be fetched, fall through and let
      // the save path surface it.
      const slug = slugify(serviceName);
      if (slug) {
        try {
          const files = await listContent('uat');
          const exists = (Array.isArray(files) ? files : []).some((f) =>
            String(f?.path || '').endsWith(`/services/${slug}.md`)
          );
          if (exists) {
            setError(
              `A service page named "${serviceName.trim()}" already exists. Open it under Pages and posts to edit it, or choose a different name to create a new one.`
            );
            setGenerating(false);
            return;
          }
        } catch {
          /* list unavailable: fall through, the save path still guards */
        }
      }

      const res = await composeServicePage({
        serviceName: serviceName.trim(),
        serviceDescription: serviceDescription.trim(),
      });
      setPage(res.page);
      // A freshly generated page is an unsaved draft again.
      setSavedPath('');
      setSavedSha('');
      setDirtySinceSave(false);
      setPreviewed(false);
      setPublished(false);
      if (res.quotaWarning) {
        setNotice(
          `You have used ${res.quotaWarning.pctOfCap}% of this month's AI allowance.`,
        );
      }
    } catch (err) {
      setError(errMessage(err, 'Could not generate the page. Please retry.'));
    } finally {
      setGenerating(false);
    }
  }, [canGenerate, serviceName, serviceDescription]);

  const handleSave = useCallback(async () => {
    if (!page || saving) return;
    setSaving(true);
    setError('');
    try {
      const res = await writeContentDraft({
        path: page.path,
        content: page.markdown,
        sha: savedSha || undefined,
      });
      setSavedPath(page.path);
      setSavedSha(res?.contentSha || '');
      setDirtySinceSave(false);
    } catch (err) {
      setError(errMessage(err, 'Could not save the draft. Please retry.'));
    } finally {
      setSaving(false);
    }
  }, [page, saving, savedSha]);

  const handlePreview = useCallback(() => {
    const uat = previewUrls?.uatUrl;
    if (!uat || !page) return;
    const url = `${uat.replace(/\/$/, '')}/services/${page.slug}/`;
    window.open(url, '_blank', 'noopener');
    setPreviewed(true);
  }, [previewUrls, page]);

  const handlePublish = useCallback(async () => {
    setConfirmOpen(false);
    if (!canPublish) return;
    setPublishing(true);
    setError('');
    try {
      await publishContent();
      setPublished(true);
    } catch (err) {
      setError(errMessage(err, 'Could not publish. Please retry.'));
    } finally {
      setPublishing(false);
    }
  }, [canPublish]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, pt: embedded ? 0 : undefined, height: '100%' }}>
      {!embedded ? (
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <AutoAwesomeRoundedIcon color="primary" />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            New service page
          </Typography>
        </Stack>
      ) : null}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Describe a service in a sentence. We will write a complete, on-brand
        service page, save it as a draft, and let you preview it on your site
        before you publish.
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}
      {notice ? (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice('')}>
          {notice}
        </Alert>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
          minHeight: 0,
        }}
      >
        {/* Brief */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <TextField
              label="Service name"
              placeholder="e.g. Drain Cleaning"
              value={serviceName}
              onChange={(e) => setServiceName(e.target.value.slice(0, 120))}
              fullWidth
              size="small"
              disabled={generating}
              inputProps={{ maxLength: 120 }}
            />
            <TextField
              label="What is it? (one or two sentences)"
              placeholder="e.g. We clear slow and blocked drains for homes and businesses."
              value={serviceDescription}
              onChange={(e) =>
                setServiceDescription(e.target.value.slice(0, 500))
              }
              fullWidth
              size="small"
              multiline
              minRows={3}
              disabled={generating}
              inputProps={{ maxLength: 500 }}
            />
            <Button
              variant="contained"
              startIcon={
                generating ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <AutoAwesomeRoundedIcon />
                )
              }
              onClick={handleGenerate}
              disabled={!canGenerate}
            >
              {generating ? 'Writing your page...' : 'Generate page'}
            </Button>
          </Stack>
        </Paper>

        {/* Preview + actions */}
        <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
          {!page ? (
            <Typography variant="body2" color="text.secondary">
              Your generated page will appear here.
            </Typography>
          ) : (
            <Stack spacing={1.5} sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {String(page.frontmatter?.title || serviceName)}
              </Typography>
              {page.frontmatter?.summary ? (
                <Typography variant="body2" color="text.secondary">
                  {String(page.frontmatter.summary)}
                </Typography>
              ) : null}
              <Divider />
              <Box
                sx={{
                  maxHeight: 280,
                  overflowY: 'auto',
                  whiteSpace: 'pre-wrap',
                  fontSize: 13.5,
                  lineHeight: 1.5,
                }}
              >
                {bodyOf(page.markdown)}
              </Box>
              <Divider />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={
                    saving ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <SaveOutlinedIcon />
                    )
                  }
                  onClick={handleSave}
                  disabled={!canSave || saving}
                >
                  {savedPath && !dirtySinceSave ? 'Saved to draft' : 'Save draft'}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<OpenInNewRoundedIcon />}
                  onClick={handlePreview}
                  disabled={!savedPath || dirtySinceSave || !previewUrls?.uatUrl}
                >
                  Preview on site
                </Button>
              </Stack>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={previewed}
                    onChange={(e) => setPreviewed(e.target.checked)}
                    disabled={!savedPath || dirtySinceSave}
                  />
                }
                label="I have previewed this page and it looks right"
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={
                  publishing ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <PublishRoundedIcon />
                  )
                }
                onClick={() => setConfirmOpen(true)}
                disabled={!canPublish}
              >
                {published ? 'Published' : 'Publish'}
              </Button>
              {published ? (
                <Alert severity="success">
                  Your service page is live. It may take a minute to appear.
                </Alert>
              ) : null}
            </Stack>
          )}
        </Paper>
      </Box>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Publish your site?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This publishes your draft, including any other unpublished changes,
            to your live site. You can always make more changes and publish
            again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handlePublish} variant="contained">
            Publish
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
